/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { illustrationNoRef } from 'assets/images';
import { ReferralData } from 'types';
import { profileSelectors } from 'store/profile/selectors';

import { WhiteContainer } from 'containers';
import { Button, Image } from 'components';

import { UserItem } from './item';

import styles from './styles.module.scss';

const MAX_VIEW_USERS = 10;
const FIRST_LVL_COPY = '0';
const FIRST_LVL = '1';
const PERCENT_LVL = ['', '8%', '5%', '5%', '6%', '7%'];

export const LevelsContainer = ({ list }: { list?: ReferralData }) => {
  const { t } = useTranslation('partner');
  const trans = useTranslation('welcome').t;
  const tStatus = useTranslation('main').t;

  const [maxReferrals, setMaxReferrals] = useState(0);
  const [displayedCount, setDisplayedCount] = useState(MAX_VIEW_USERS);
  const [activeUser, setActiveUser] = useState(0);
  const [childrenUser, setChildrenUser] = useState<number[]>([]);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [allTeamSales, setAllTeamSales] = useState(0);

  const { rank } = useSelector(profileSelectors.getState);

  // Create a sorted clone of list (descending by joined_at).
  const sortedList = useMemo<ReferralData | null>(() => {
    if (!list) return null;

    // deep clone - prefer structuredClone if available, fallback to JSON
    const clone = (typeof structuredClone === 'function')
      ? (structuredClone(list) as ReferralData)
      : (JSON.parse(JSON.stringify(list)) as ReferralData);

    Object.keys(clone).forEach((level) => {
      const lvl = (clone as any)[level];
      if (lvl?.referrals && Array.isArray(lvl.referrals)) {
        lvl.referrals.sort((a: any, b: any) => {
          const aTime = a.joined_at ? new Date(a.joined_at).getTime() : 0;
          const bTime = b.joined_at ? new Date(b.joined_at).getTime() : 0;
          return bTime - aTime; // DESCENDING — newest first
        });
      }
    });

    return clone;
  }, [list]);

  const handleOpen = useCallback((index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  }, []);

  const handleInId = useCallback((level: number, userId: number) => {
    setActiveUser(userId);

    // if no sortedList, no children to compute
    if (!sortedList) {
      setChildrenUser([]);
      return;
    }

    const descendants: number[] = [];
    const nextLevel = String(level + 1);

    sortedList[nextLevel]?.referrals?.forEach((user: any) => {
      if (user.parent_id === userId) {
        descendants.push(user.id);
      }
    });

    setChildrenUser(descendants);
  }, [sortedList]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    const className = target?.className ?? '';

    const isModalClick = typeof className === 'string' && className.indexOf('structure_lvl_modal') !== -1;
    const isIconClick = typeof className === 'string' && className.indexOf('structure_lvl_users_item_info') !== -1;

    if (!(isModalClick || isIconClick)) {
      handleOpen(1.1);
    }
  }, [handleOpen]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleShowMore = useCallback(() => {
    setDisplayedCount((prevCount) => Math.min(prevCount + 6, maxReferrals));
  }, [maxReferrals]);

  useEffect(() => {
    if (!list) {
      setAllTeamSales(0);
      setMaxReferrals(0);
      return;
    }

    const countsRefs: number[] = [];
    let totalSales = 0;

    const keysCount = Object.keys(list).length;
    for (let i = 0; i < keysCount; i += 1) {
      if (i !== Number(FIRST_LVL_COPY)) {
        totalSales += (list[i]?.licenses || 0);
      }

      if (list[i]?.referrals) {
        countsRefs.push(list[i].referrals.length);
      }
    }

    setAllTeamSales(totalSales);
    setMaxReferrals(countsRefs.length ? Math.max(...countsRefs) : 0);
  }, [list]);

  const users = useCallback((level: string, isFirst: boolean) => {
    if (!sortedList || !sortedList[level]?.referrals) {
      return (
        <div className={styles.structure_lvl_none}>
          {t('No_referrals_')}
        </div>
      );
    }

    return sortedList[level].referrals.slice(0, displayedCount).map((i: any) => (
      <UserItem
        key={i.id}
        data={i}
        isOpen={openIndex === i.id}
        onOpen={() => handleOpen(i.id)}
        clickInId={() => handleInId(Number(level), i.id)}
        activeUser={i.id === activeUser}
        activeUserParent={childrenUser.includes(i.id)}
        isFirst={isFirst}
      />
    ));
  }, [
    t,
    sortedList,
    displayedCount,
    openIndex,
    handleOpen,
    handleInId,
    activeUser,
    childrenUser,
  ]);

  const statusMain = useMemo(() => (rank !== 'None' ? rank : undefined), [rank]);

  if (!sortedList) {
    return (
      <div className={styles.td_no_data_div}>
        <Image
          url={illustrationNoRef}
          className={styles.td_no_data_div_icon}
        />
        <div>
          {t('Referral_list_is_empty_')}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.structure_lvl}>
        <div className={styles.structure_lvl_container}>
          {Object.keys(sortedList).map((item) => (
            <WhiteContainer
              className={styles.structure_lvl_content}
              key={item}
            >
              <div
                className={cn(styles.structure_lvl_top, {
                  [styles.structure_lvl_top_first]: item === FIRST_LVL_COPY,
                  [styles.structure_lvl_top_status]: !!statusMain,
                })}
              >
                <div className={styles.structure_lvl_title}>
                  {item === FIRST_LVL_COPY ? (
                    <div>
                      {t('Direct_sales_')}
                      <br />
                      <span className={styles.structure_lvl_title_percent}>
                        16%
                      </span>
                    </div>
                  ) : (
                    <>
                      {item}
                      {' '}
                      {trans('Level_')}
                      {' '}
                      <span className={styles.structure_lvl_title_orange}>
                        {PERCENT_LVL[Number(item)]}
                      </span>
                    </>
                  )}
                </div>

                {item === FIRST_LVL_COPY && <div className={styles.structure_line} />}

                <div className={styles.structure_lvl_top_info}>
                  <div className={styles.structure_lvl_top_info_label}>
                    {t(item === FIRST_LVL_COPY ? 'Licenses_full_' : 'Team_')}
                  </div>
                  <div className={styles.structure_lvl_top_info_value}>
                    {item === FIRST_LVL_COPY ? sortedList[item]?.direct_licenses
                      : sortedList[item]?.count || 0}
                    {' '}
                    {t(item === FIRST_LVL_COPY ? '' : 'Agents_')}
                  </div>
                </div>

                <div className={styles.structure_line} />

                {item === FIRST_LVL_COPY && (
                  <div className={cn(styles.structure_lvl_top_info)}>
                    <div className={styles.structure_lvl_top_info_label}>
                      {t('Licenses_sold_by_team_')}
                    </div>
                    <div className={styles.structure_lvl_top_info_value}>
                      {allTeamSales || 0}
                      {' '}
                      {t(item === FIRST_LVL_COPY ? '' : 'Agents_')}
                    </div>
                  </div>
                )}

                {item === FIRST_LVL_COPY && statusMain && (
                  <div className={styles.structure_line} />
                )}

                {item === FIRST_LVL_COPY && statusMain && (
                  <div className={styles.structure_lvl_top_info}>
                    <div className={styles.structure_lvl_top_info_value_status}>
                      {tStatus(statusMain)}
                    </div>
                  </div>
                )}

                {item !== FIRST_LVL_COPY && (
                  <div className={styles.structure_lvl_title}>
                    {t('Licenses_full_')}
                  </div>
                )}

                <div
                  className={cn(styles.structure_lvl_top_info, {
                    [styles.structure_lvl_top_info_first]: item === FIRST_LVL_COPY,
                  })}
                >
                  <div className={styles.structure_lvl_top_info_label}>
                    {t('Sales_team_')}
                  </div>
                  <div className={styles.structure_lvl_top_info_value}>
                    {sortedList[item]?.licenses || 0}
                  </div>
                </div>

                {item !== FIRST_LVL && (
                  <div
                    className={cn(styles.structure_lvl_top_info, {
                      [styles.structure_lvl_top_info_first]:
                      !sortedList[item]?.direct_licenses || item === FIRST_LVL_COPY,
                    })}
                  >
                    <div className={styles.structure_lvl_top_info_label}>
                      {t('Direct_sales_')}
                    </div>
                    <div className={styles.structure_lvl_top_info_value}>
                      {sortedList[item]?.direct_licenses || 0}
                    </div>
                  </div>
                )}

              </div>

              <div className={styles.structure_lvl_users}>
                {users(item, item === FIRST_LVL_COPY)}
              </div>
            </WhiteContainer>
          ))}
        </div>
      </div>

      {maxReferrals > displayedCount && (
        <div className={styles.pagination_container}>
          <Button
            onClick={handleShowMore}
            className={styles.structure_show_more_btn}
            color="pink"
          >
            {t('Show_more_')}
          </Button>
        </div>
      )}
    </>
  );
};
